import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  LockKeyhole,
  LoaderCircle,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Search,
} from "lucide-react";
import asendiaLogo from "../../assets/images/asendia_logos/logo-mark-transparent-512.png";
import { fetchReview, formatTime, mediaUrl, timestamp } from "./reviewApi";
import { chapterAnswerTexts, chapterStart } from "./reviewAnswers";
import "./review.css";

// Temporarily hidden until the viewer is connected to existing assessment reports.
const ASSESSMENT_TAB_ENABLED = false;

function Assessment({ value }) {
  if (!value)
    return (
      <div className="review-empty">
        <h3>Assessment not available here yet</h3>
        <p>
          This recording does not have a saved TutorMe assessment for this
          interview attempt. The original report may still be available in
          Workable.
        </p>
      </div>
    );
  return (
    <div className="review-detail">
      <div className="review-eyebrow">
        AI-generated assessment · {value.source}
      </div>
      <p className="review-muted">
        Decision support, not a hiring decision. Check the recording and resume
        before drawing conclusions.
      </p>
      {(value.incomplete || value.partial) && (
        <p className="review-notice">
          {value.incomplete
            ? "Incomplete evaluation — automated steps did not finish successfully."
            : "Partial assessment — some rubric categories were not assessed."}{" "}
          Review the evidence before using this score.
        </p>
      )}
      <div className="review-score">
        <strong>{value.score ?? "—"}</strong>
        <span>{value.maxScore ? ` / ${value.maxScore}` : ""}</span>
        <p>{value.recommendation}</p>
      </div>
      {value.hardFail && (
        <p className="review-notice">
          Rubric flag:{" "}
          {value.hardFailReason || "Review the assessment flags in Workable."}
        </p>
      )}
      {value.credentialCapNote && (
        <p className="review-notice">
          Credential score cap: {value.credentialCapNote}
        </p>
      )}
      {value.flags?.length > 0 && (
        <section>
          <h3>Assessment flags</h3>
          <ul>
            {value.flags.map((flag, i) => (
              <li key={i}>{flag}</li>
            ))}
          </ul>
        </section>
      )}
      <p>{value.summary}</p>
      {["strengths", "concerns"].map((key) =>
        value[key]?.length ? (
          <section key={key}>
            <h3>{key === "strengths" ? "Strengths" : "Points to review"}</h3>
            <ul>
              {value[key].map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>
        ) : null,
      )}
      {value.categories?.map((category, i) => (
        <article className="review-rubric" key={i}>
          <div>
            <h3>{category.name}</h3>
            <strong>
              {category.assessed
                ? `${category.score ?? "—"}${category.maxScore ? ` / ${category.maxScore}` : ""}`
                : "Not assessed"}
            </strong>
          </div>
          <p>{category.rationale}</p>
        </article>
      ))}
    </div>
  );
}

function Resume({ value, available }) {
  if (!available)
    return (
      <div className="review-empty">
        <h3>No resume available</h3>
        <p>A resume has not been attached to this review.</p>
      </div>
    );
  const url = mediaUrl(value?.url);
  return (
    <div className="review-detail">
      <div className="review-section-heading">
        <h3>Candidate resume</h3>
        {url && (
          <a
            className="review-button"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open original <ExternalLink size={14} aria-hidden="true" />
          </a>
        )}
      </div>
      {url && value.format === "pdf" && (
        <iframe
          className="review-resume-frame"
          src={`${url}#view=FitH&navpanes=0`}
          title="Candidate resume PDF"
          referrerPolicy="no-referrer"
        />
      )}
      {url && value.format === "pdf" && (
        <p className="review-muted">
          If the PDF does not display, use “Open original”.
        </p>
      )}
      <p>{value?.summary}</p>
      {["experience", "education"].map((key) =>
        value?.[key]?.length ? (
          <section key={key}>
            <h3>{key === "experience" ? "Experience" : "Education"}</h3>
            {value[key].map((row, i) => (
              <article className="review-rubric" key={i}>
                <strong>{row.role || row.degree}</strong>
                <p>{row.company || row.institution}</p>
                <small>
                  {key === "experience"
                    ? [row.start_date, row.end_date].filter(Boolean).join(" – ")
                    : row.field_of_study}
                </small>
              </article>
            ))}
          </section>
        ) : null,
      )}
    </div>
  );
}

export default function PublicInterviewShare({
  token,
  phone = false,
  audioOnly = false,
  initialRequest,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("questions");
  const [detail, setDetail] = useState({});
  const [detailAttempt, setDetailAttempt] = useState(0);
  const [query, setQuery] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [mediaStatus, setMediaStatus] = useState("loading");
  const [playError, setPlayError] = useState("");
  const [rate, setRate] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const [startingPlayback, setStartingPlayback] = useState(false);
  const [loadedThumbnail, setLoadedThumbnail] = useState(null);
  const [failedThumbnail, setFailedThumbnail] = useState(null);
  const mediaRef = useRef(null);
  const pendingSeek = useRef(null);
  const activeTranscript = useRef(null);
  const activeQuestion = useRef(null);
  const questionPanel = useRef(null);
  const mounted = useRef(true);
  const refreshLock = useRef(false);
  const requestVersion = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
    };
  }, []);

  const load = useCallback(
    async (initial) => {
      const version = ++requestVersion.current;
      setLoading(true);
      setError(null);
      try {
        const result = initial
          ? await initial
          : { data: await fetchReview(token) };
        if (result.error) throw result.error;
        if (mounted.current && version === requestVersion.current)
          setData(result.data);
      } catch (e) {
        if (mounted.current && version === requestVersion.current) {
          setError(e);
          setData(null);
        }
      } finally {
        if (mounted.current && version === requestVersion.current)
          setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    load(initialRequest);
  }, [load, initialRequest]);
  useEffect(() => {
    const previous = document.title;
    document.title = "Interview review | Asendia AI";
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
      document.title = previous;
    };
  }, []);

  const permissions = data?.review?.permissions || {};
  const resumeAllowed = permissions.resume === true;
  const assessmentAllowed =
    ASSESSMENT_TAB_ENABLED && permissions.assessment === true;
  useEffect(() => {
    if (
      (tab !== "resume" && tab !== "assessment") ||
      (tab === "resume" ? !resumeAllowed : !assessmentAllowed)
    )
      return;
    let cancelled = false;
    setDetail({ loading: true });
    // Revalidate authorization whenever a private tab opens; no persistent cache.
    fetchReview(token, tab).then(
      (value) => {
        if (!cancelled) setDetail({ value });
      },
      (e) => {
        if (!cancelled) {
          setDetail({ error: e.message });
          if (e.status === 404 || e.status === 410) {
            setData(null);
            setError(e);
          }
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [tab, token, detailAttempt, resumeAllowed, assessmentAllowed]);

  const candidate = data?.candidate || {};
  const video = mediaUrl(candidate.videoUrl);
  const audio = mediaUrl(candidate.audioUrl);
  // Phone screens only have audio. The ?audio=1 fallback plays the stored
  // video file's audio track: the raw call recording may sit in private
  // storage the browser cannot fetch.
  const source = phone ? audio || video : video || audio;
  // An mp4 plays fine inside <audio>; audio-only links never render the video.
  const isVideo = Boolean(video && (!phone || !audio)) && !audioOnly;
  const thumbnail = mediaUrl(candidate.thumbnailUrl);
  const previewImage =
    thumbnail && failedThumbnail !== thumbnail ? thumbnail : null;
  const hasPreviewImage = previewImage && loadedThumbnail === previewImage;
  const transcript = useMemo(
    () =>
      (Array.isArray(candidate.transcriptObject)
        ? candidate.transcriptObject
        : []
      ).filter(
        (row) =>
          row && typeof row === "object" && typeof row.content === "string",
      ),
    [candidate.transcriptObject],
  );
  const chapters = useMemo(
    () => (Array.isArray(data?.review?.chapters) ? data.review.chapters : []),
    [data?.review?.chapters],
  );
  const playable = useMemo(
    () =>
      chapters
        .filter(
          (row) =>
            timestamp(row.answerStart) &&
            (!duration || row.answerStart < duration),
        )
        .sort((a, b) => a.answerStart - b.answerStart),
    [chapters, duration],
  );
  const activeChapter = playable.reduce(
    (last, row, index) => (currentTime >= chapterStart(row) ? index : last),
    -1,
  );
  const answerTexts = useMemo(
    () => chapterAnswerTexts(chapters, candidate.transcriptObject),
    [chapters, candidate.transcriptObject],
  );
  useEffect(() => {
    if (tab !== "questions" || activeChapter < 0) return;
    const panel = questionPanel.current;
    const question = activeQuestion.current;
    if (!panel || !question) return;
    const panelBounds = panel.getBoundingClientRect();
    const questionBounds = question.getBoundingClientRect();
    if (
      questionBounds.top < panelBounds.top ||
      questionBounds.bottom > panelBounds.bottom
    ) {
      // Move only the question pane, never the page/video. Run once when the
      // active question changes so manual scrolling within an answer is kept.
      panel.scrollTo?.({
        top: Math.max(
          0,
          panel.scrollTop + questionBounds.top - panelBounds.top - 12,
        ),
        behavior: "auto",
      });
    }
  }, [activeChapter, tab, answerTexts]);
  const activeLine = transcript.reduce(
    (last, row, index) =>
      timestamp(row.time_start) && row.time_start <= currentTime ? index : last,
    -1,
  );
  const filteredTranscript = transcript
    .map((row, index) => ({ ...row, index }))
    .filter((row) => row.content.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    if (data && !chapters.length && tab === "questions") setTab("transcript");
    if (data && tab === "assessment" && !assessmentAllowed)
      setTab(chapters.length ? "questions" : "transcript");
  }, [data, chapters.length, tab, assessmentAllowed]);

  async function play() {
    const media = mediaRef.current;
    if (!media) return;
    setPlayError("");
    setStartingPlayback(true);
    try {
      await media.play();
    } catch (e) {
      if (mounted.current) setStartingPlayback(false);
      if (mounted.current && e.name !== "AbortError")
        setPlayError(
          "Playback could not start. Press Play or retry the recording.",
        );
    }
  }

  function seek(value, startPlaying = true) {
    const media = mediaRef.current;
    if (!media || !timestamp(value)) return;
    const target = Math.max(
      0,
      Math.min(
        Number(value),
        Number.isFinite(media.duration)
          ? Math.max(0, media.duration - 0.05)
          : Number(value),
      ),
    );
    if (media.readyState < 1) {
      if (startPlaying) setStartingPlayback(true);
      pendingSeek.current = { time: target, play: startPlaying };
      media.load();
    } else {
      media.currentTime = target;
      setCurrentTime(target);
      if (startPlaying) play();
    }
  }

  function ready() {
    const media = mediaRef.current;
    if (!media) return;
    if (Number.isFinite(media.duration)) setDuration(media.duration);
    media.playbackRate = rate;
    setMediaStatus(media.readyState >= 2 ? "ready" : "loading");
    if (pendingSeek.current) {
      const pending = pendingSeek.current;
      pendingSeek.current = null;
      seek(pending.time, pending.play);
    }
  }

  async function refreshMedia() {
    if (refreshLock.current) return;
    refreshLock.current = true;
    setRefreshing(true);
    setPlayError("");
    const media = mediaRef.current;
    const position = media?.currentTime || currentTime;
    const shouldPlay = media ? !media.paused : false;
    try {
      const fresh = await fetchReview(token);
      if (!mounted.current) return;
      const next = mediaUrl(
        phone
          ? fresh.candidate?.audioUrl || fresh.candidate?.videoUrl
          : fresh.candidate?.videoUrl || fresh.candidate?.audioUrl,
      );
      pendingSeek.current = { time: position, play: shouldPlay };
      setData(fresh);
      setMediaStatus("loading");
      if (next === source && media) media.load();
    } catch (e) {
      if (!mounted.current) return;
      if (e.status === 404 || e.status === 410) {
        setData(null);
        setError(e);
      } else setPlayError(e.message);
    } finally {
      refreshLock.current = false;
      if (mounted.current) setRefreshing(false);
    }
  }

  function keyboard(event) {
    if (
      event.target.closest("button, input, select, textarea, a") ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.target.tagName === "VIDEO" ||
      event.target.tagName === "AUDIO"
    )
      return;
    if (event.key === " ") {
      event.preventDefault();
      playing ? mediaRef.current?.pause() : play();
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      seek(currentTime + (event.key === "ArrowLeft" ? -10 : 10), playing);
    }
  }

  const mediaProps = {
    ref: mediaRef,
    src: source,
    controls: true,
    preload: "auto",
    playsInline: true,
    onLoadedMetadata: ready,
    onLoadedData: () => setMediaStatus("ready"),
    onCanPlay: () => setMediaStatus("ready"),
    onWaiting: () => setMediaStatus("buffering"),
    onStalled: () => setMediaStatus("buffering"),
    onSeeking: () => setMediaStatus("buffering"),
    onSeeked: () => setMediaStatus("ready"),
    onPlaying: () => {
      setPlaying(true);
      setPreviewDismissed(true);
      setStartingPlayback(false);
      setMediaStatus("ready");
      setPlayError("");
    },
    onPause: () => {
      setPlaying(false);
      setStartingPlayback(false);
    },
    onEnded: () => setPlaying(false),
    onError: () => {
      setMediaStatus("error");
      setStartingPlayback(false);
    },
    onTimeUpdate: (e) => setCurrentTime(e.currentTarget.currentTime),
    onRateChange: (e) => setRate(e.currentTarget.playbackRate),
  };

  return (
    <div className="review-page">
      <header className="review-header">
        <a
          href="https://www.asendia.ai"
          rel="noreferrer"
          className="review-brand"
          aria-label="Asendia AI website"
        >
          <img src={asendiaLogo} width="32" height="32" alt="" />
          <span>Asendia AI</span>
        </a>
        <span className="review-header-label">Interview review</span>
        <span className="review-private">
          <LockKeyhole size={13} aria-hidden="true" /> Shared securely
        </span>
      </header>
      <main className="review-main">
        {loading && !data ? (
          <div role="status" className="review-skeleton">
            <h1>Preparing your interview review</h1>
            <p>Loading candidate details and recording…</p>
            <div className="review-skeleton-grid">
              <div />
              <div />
            </div>
          </div>
        ) : error ? (
          <div role="alert" className="review-empty">
            <h1>Interview unavailable</h1>
            <p>{error.message}</p>
            {error.status !== 404 && error.status !== 410 && (
              <button className="review-button primary" onClick={() => load()}>
                Try again
              </button>
            )}
          </div>
        ) : (
          data && (
            <>
              <section className="review-heading">
                <div>
                  <div className="review-eyebrow">
                    {data.branding?.companyName || "Shared interview"}
                    <span aria-hidden="true">/</span>
                    {phone ? "Phone screening" : "Video interview"}
                  </div>
                  <h1>{candidate.displayName || "Candidate"}</h1>
                  <p>
                    {candidate.jobTitle || "Interview recording"}
                    {duration > 0 && (
                      <span className="review-duration">
                        {formatTime(duration)} recording
                      </span>
                    )}
                  </p>
                </div>
              </section>
              {data.expiresAt && (
                <p className="review-expiry">
                  Link access expires{" "}
                  {new Date(data.expiresAt).toLocaleString()}.
                </p>
              )}
              <div className="review-workspace">
                <section
                  className="review-player-panel"
                  aria-label="Recording player"
                  aria-describedby="review-keyboard-help"
                  tabIndex={0}
                  onKeyDown={keyboard}
                >
                  <div className="review-player-heading">
                    <h2>{phone ? "Phone recording" : "Interview recording"}</h2>
                    {source && (
                      <div className="review-player-status">
                        <span
                          className={`review-media-state ${mediaStatus}`}
                          role="status"
                          aria-live="polite"
                        >
                          <span
                            className="review-status-dot"
                            aria-hidden="true"
                          />
                          {mediaStatus === "loading"
                            ? "Loading recording…"
                            : mediaStatus === "buffering"
                              ? "Buffering…"
                              : mediaStatus === "error"
                                ? "The recording could not load."
                                : playing
                                  ? "Playing"
                                  : "Ready to play"}
                        </span>
                        <button
                          className="review-icon-button review-retry"
                          onClick={refreshMedia}
                          disabled={refreshing}
                          aria-label={
                            refreshing
                              ? "Retrying recording"
                              : "Retry recording"
                          }
                          title="Retry recording"
                        >
                          <RefreshCw
                            size={15}
                            className={refreshing ? "review-spin" : ""}
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    )}
                  </div>
                  <div
                    className={`review-media ${!isVideo ? "review-audio" : ""}`}
                  >
                    {source ? (
                      isVideo ? (
                        <video
                          {...mediaProps}
                          aria-label="Interview recording"
                          tabIndex={
                            hasPreviewImage && !previewDismissed ? -1 : 0
                          }
                        />
                      ) : (
                        <>
                          <p>
                            {phone
                              ? "Phone screening recording"
                              : audioOnly
                                ? "Interview audio"
                                : "Video is processing — audio is available"}
                          </p>
                          <audio {...mediaProps} aria-label="Interview audio" />
                        </>
                      )
                    ) : (
                      <div className="review-media-empty">
                        <h2>Recording is not available yet</h2>
                        <p>Check again shortly without leaving this page.</p>
                        <button
                          className="review-button"
                          disabled={refreshing}
                          onClick={refreshMedia}
                        >
                          Check again
                        </button>
                      </div>
                    )}
                    {source && isVideo && !previewDismissed && previewImage && (
                      <div className="review-preview" hidden={!hasPreviewImage}>
                        <img
                          key={previewImage}
                          src={previewImage}
                          alt="Interview preview"
                          referrerPolicy="no-referrer"
                          onLoad={() => setLoadedThumbnail(previewImage)}
                          onError={() => setFailedThumbnail(previewImage)}
                        />
                        <div className="review-preview-content">
                          <button
                            className="review-preview-play"
                            aria-label={
                              startingPlayback
                                ? "Starting playback"
                                : "Play interview"
                            }
                            disabled={startingPlayback}
                            onClick={() => seek(0)}
                          >
                            {startingPlayback ? (
                              <LoaderCircle
                                size={24}
                                className="review-spin"
                                aria-hidden="true"
                              />
                            ) : (
                              <Play
                                size={24}
                                fill="currentColor"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                          <span className="review-preview-caption">
                            {startingPlayback
                              ? "Starting recording…"
                              : "Play interview recording"}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {source && (
                    <>
                      {playError && (
                        <p role="alert" className="review-notice">
                          {playError}
                        </p>
                      )}
                      <div className="review-controls">
                        <div
                          className="review-playback-controls"
                          role="group"
                          aria-label="Playback controls"
                        >
                          <button
                            className="review-icon-button review-skip"
                            onClick={() => seek(currentTime - 10, playing)}
                            aria-label="Back 10 seconds"
                            title="Back 10 seconds"
                          >
                            <RotateCcw size={22} aria-hidden="true" />
                            <span aria-hidden="true">10</span>
                          </button>
                          <button
                            className="review-icon-button review-play"
                            aria-label={playing ? "Pause" : "Play"}
                            title={playing ? "Pause" : "Play"}
                            onClick={() =>
                              playing ? mediaRef.current?.pause() : play()
                            }
                          >
                            {playing ? (
                              <Pause
                                size={17}
                                fill="currentColor"
                                aria-hidden="true"
                              />
                            ) : (
                              <Play
                                size={17}
                                fill="currentColor"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                          <button
                            className="review-icon-button review-skip"
                            onClick={() => seek(currentTime + 10, playing)}
                            aria-label="Forward 10 seconds"
                            title="Forward 10 seconds"
                          >
                            <RotateCw size={22} aria-hidden="true" />
                            <span aria-hidden="true">10</span>
                          </button>
                          <label className="review-speed">
                            <span className="review-sr-only">Speed</span>
                            <select
                              value={rate}
                              onChange={(e) => {
                                const next = Number(e.target.value);
                                setRate(next);
                                if (mediaRef.current)
                                  mediaRef.current.playbackRate = next;
                              }}
                            >
                              {[1, 1.25, 1.5, 1.75, 2].map((value) => (
                                <option key={value} value={value}>
                                  {value}×
                                </option>
                              ))}
                            </select>
                            <ChevronDown size={12} aria-hidden="true" />
                          </label>
                        </div>
                        <div
                          className="review-answer-controls"
                          role="group"
                          aria-label="Answer navigation"
                        >
                          <button
                            className="review-icon-button"
                            aria-label="Previous answer"
                            title="Previous answer"
                            disabled={activeChapter <= 0}
                            onClick={() =>
                              seek(playable[activeChapter - 1]?.answerStart)
                            }
                          >
                            <ChevronLeft size={18} aria-hidden="true" />
                          </button>
                          <span>
                            {activeChapter >= 0 ? (
                              <>
                                Answer <strong>{activeChapter + 1}</strong>
                                <span className="review-answer-total">
                                  {" "}
                                  / {playable.length}
                                </span>
                              </>
                            ) : (
                              "Full recording"
                            )}
                          </span>
                          <button
                            className="review-icon-button"
                            aria-label="Next answer"
                            title="Next answer"
                            disabled={activeChapter >= playable.length - 1}
                            onClick={() =>
                              seek(playable[activeChapter + 1]?.answerStart)
                            }
                          >
                            <ChevronRight size={18} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      {playable.length > 0 && (
                        <div className="review-current-question">
                          <span>
                            {activeChapter >= 0
                              ? "Current question"
                              : "Up first"}
                          </span>
                          <p>{playable[Math.max(0, activeChapter)].question}</p>
                        </div>
                      )}
                    </>
                  )}
                  <p id="review-keyboard-help" className="review-sr-only">
                    With this panel focused, press Space to play or pause. Use
                    the left and right arrow keys to skip 10 seconds.
                  </p>
                </section>
                <section
                  className="review-content-panel"
                  aria-label="Review information"
                >
                  {audioOnly ? (
                    // Client-facing audio links (?audio=1) share the recording
                    // only: the recruiting agency does not want end clients
                    // reading questions or transcripts (7 Oct 2026 review).
                    <div className="review-empty review-audio-note">
                      <p>
                        This link shares the interview audio only. Interview
                        questions and the transcript are not included in this
                        view.
                      </p>
                    </div>
                  ) : (
                    <>
                  <nav className="review-tabs" aria-label="Review sections">
                    {[
                      ...(chapters.length
                        ? [
                            [
                              "questions",
                              data.review?.kind === "conversation"
                                ? "Conversation"
                                : "Questions",
                            ],
                          ]
                        : []),
                      ["transcript", "Transcript"],
                      ...(resumeAllowed ? [["resume", "Resume"]] : []),
                      ...(assessmentAllowed
                        ? [["assessment", "Assessment"]]
                        : []),
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        className={tab === key ? "active" : ""}
                        aria-current={tab === key ? "page" : undefined}
                        onClick={() => setTab(key)}
                      >
                        {label}
                        {key === "questions" && <span>{chapters.length}</span>}
                      </button>
                    ))}
                  </nav>
                  <div className="review-content" key={tab} ref={questionPanel}>
                    {tab === "questions" && (
                      <>
                        <div className="review-panel-intro">
                          <p>Select a question to play from its beginning.</p>
                        </div>
                        <ol className="review-questions">
                          {chapters.map((row, index) => {
                            const canPlay =
                              timestamp(row.questionStart) &&
                              (!duration || row.questionStart < duration);
                            const active =
                              playable[activeChapter]?.id === row.id;
                            return (
                              <li
                                key={row.id || index}
                                ref={active ? activeQuestion : null}
                                className={active ? "active" : ""}
                                aria-current={active ? "step" : undefined}
                              >
                                <div className="review-question-number">
                                  {String(index + 1).padStart(2, "0")}
                                </div>
                                <div>
                                  <button
                                    type="button"
                                    className="review-question-text"
                                    disabled={!source || !canPlay}
                                    title={
                                      !source
                                        ? "Recording unavailable"
                                        : !canPlay
                                          ? "Question timestamp unavailable"
                                          : "Play from this question"
                                    }
                                    onClick={() => seek(row.questionStart)}
                                  >
                                    {active ? (
                                      <strong>{row.question}</strong>
                                    ) : (
                                      row.question
                                    )}
                                  </button>
                                  {active && (
                                    <section
                                      className="review-active-answer"
                                      aria-label="Candidate answer"
                                    >
                                      <h3>Candidate’s answer</h3>
                                      <p>
                                        {answerTexts[index] ||
                                          "Answer text is unavailable for this question. You can still listen to the recording."}
                                      </p>
                                    </section>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                      </>
                    )}
                    {tab === "transcript" && (
                      <>
                        <div className="review-transcript-tools">
                          <label htmlFor="review-search">
                            Search transcript
                          </label>
                          <div className="review-search-field">
                            <Search size={16} aria-hidden="true" />
                            <input
                              id="review-search"
                              type="search"
                              placeholder="Find a topic or phrase…"
                              value={query}
                              onChange={(e) => setQuery(e.target.value)}
                            />
                          </div>
                          <button
                            className="review-link"
                            onClick={() => {
                              setQuery("");
                              setTimeout(
                                () =>
                                  activeTranscript.current?.scrollIntoView({
                                    block: "nearest",
                                    behavior: "smooth",
                                  }),
                                0,
                              );
                            }}
                          >
                            Jump to current moment
                          </button>
                        </div>
                        {filteredTranscript.length ? (
                          <ol className="review-transcript">
                            {filteredTranscript.map((row) => (
                              <li
                                key={row.index}
                                ref={
                                  row.index === activeLine
                                    ? activeTranscript
                                    : null
                                }
                              >
                                <button
                                  className={
                                    row.index === activeLine ? "active" : ""
                                  }
                                  disabled={
                                    !source || !timestamp(row.time_start)
                                  }
                                  onClick={() => seek(row.time_start)}
                                >
                                  <span>
                                    <strong>
                                      {[
                                        "agent",
                                        "assistant",
                                        "interviewer",
                                      ].includes(row.role)
                                        ? "Interviewer"
                                        : "Candidate"}
                                    </strong>
                                    <time>
                                      {timestamp(row.time_start)
                                        ? formatTime(row.time_start)
                                        : "Time unavailable"}
                                    </time>
                                  </span>
                                  <p>{row.content}</p>
                                </button>
                              </li>
                            ))}
                          </ol>
                        ) : (
                          <div className="review-empty">
                            <p>
                              {query
                                ? "No matching transcript entries."
                                : "No transcript is available for this recording."}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                    {((tab === "resume" && resumeAllowed) ||
                      (tab === "assessment" && assessmentAllowed)) &&
                      (detail.loading ? (
                        <p role="status" className="review-detail">
                          Loading {tab}…
                        </p>
                      ) : detail.error ? (
                        <div role="alert" className="review-empty">
                          <p>{detail.error}</p>
                          <button
                            className="review-button"
                            onClick={() => setDetailAttempt((n) => n + 1)}
                          >
                            Try again
                          </button>
                        </div>
                      ) : detail.value ? (
                        tab === "resume" ? (
                          <Resume
                            value={detail.value.resume}
                            available={detail.value.available}
                          />
                        ) : (
                          <Assessment value={detail.value.assessment} />
                        )
                      ) : null)}
                  </div>
                    </>
                  )}
                </section>
              </div>
              <footer className="review-footer">
                <LockKeyhole size={12} aria-hidden="true" />
                Confidential candidate information · Only share this link with
                authorized reviewers.
              </footer>
            </>
          )
        )}
      </main>
    </div>
  );
}
